import { CSSProperties } from "react";
import { ColorType, palette } from "@jnpmedi/grebob";
import { DocumentSignerAttrType, DocumentSignerInputItemCss, DocumentSignerSignature } from "@maven-rest/docs/DocumentSign.dto";
import { DocsPdfMetainfo } from "@maven-rest/docs/pdf/DocsPdf.dto";

export enum SignatureType {
  Sign = "SIGN",
  Stamp = "STAMP",
}

export enum SigningMethod {
  AtOnce = "AT_ONCE",
  Each = "EACH",
}

export const scrollTo = (element: HTMLElement, offset: number) => {
  const offsetTop = element.getBoundingClientRect().top + offset;
  const duration = Math.min(0.8, Math.max(0.15, (Math.abs(offsetTop) / 200) * 0.1));
  const scrollTop = offsetTop + window.scrollY;

  document.body.style.scrollBehavior = "smooth";

  window.scrollTo({
    top: scrollTop,
    behavior: "smooth",
  });

  setTimeout(() => {
    document.body.style.scrollBehavior = "unset";
  }, duration * 1000);
};

export interface DocumentSignerSignatureExt extends DocumentSignerSignature {
  signature: string | StampImageSignature;
}

export interface StampImageSignature {
  key: string;
  bucket: string;
  stampBase64: string;
}

export const inputItemCss: DocumentSignerInputItemCss = {
  width: 160,
  height: 24,
  fontSize: 10,
  padding: [6, 6, 6, 6], // NOTE: div 기본 padding
  lineHeight: 135,
};

export type MarkerColorType = {
  text: ColorType;
  border: ColorType;
  background: CSSProperties["backgroundColor"];
  backgroundHover: CSSProperties["backgroundColor"];
};

export const defaultMarkerColor: {
  sign: MarkerColorType;
  common: MarkerColorType;
  additional: MarkerColorType;
} = {
  sign: {
    text: "yellow080",
    border: "yellow050",
    background: "rgba(255, 236, 134, 0.85)",
    backgroundHover: palette["yellow030"],
  },
  common: {
    text: "grey090",
    border: "yellow050",
    background: palette["grey020"],
    backgroundHover: palette["grey020"],
  },
  additional: {
    text: "grey090",
    border: "yellow050",
    background: palette["white"],
    backgroundHover: palette["white"],
  },
};

const markerColors: MarkerColorType[] = [
  {
    border: "maven-blue-dark",
    text: "blue080",
    background: "rgba(178, 229, 255, 0.85)",
    backgroundHover: "rgba(178, 229, 255, 1)",
  },
  {
    border: "warning-primary",
    text: "orange080",
    background: "rgba(255, 217, 190, 0.85)",
    backgroundHover: "rgba(255, 217, 190, 1)",
  },
  {
    border: "positive-primary",
    text: "green050",
    background: "rgba(167, 240, 186, 0.85)",
    backgroundHover: "rgba(167, 240, 186, 1)",
  },
  {
    border: "yellow050",
    text: "yellow080",
    background: "rgba(255, 243, 179, 0.85)",
    backgroundHover: "rgba(255, 243, 179, 1)",
  },
  {
    border: "purple050",
    text: "purple080",
    background: "rgba(232, 218, 255, 0.85)",
    backgroundHover: "rgba(232, 218, 255, 1)",
  },
  {
    border: "darkNavy040",
    text: "darkNavy080",
    background: "rgba(185, 225, 255, 0.85)",
    backgroundHover: "rgba(185, 225, 255, 1)",
  },
  {
    border: "blue030",
    text: "blue080",
    background: "rgba(222, 244, 255, 0.85)",
    backgroundHover: "rgba(222, 244, 255, 1)",
  },
  {
    border: "orange030",
    text: "orange080",
    background: "rgba(255, 233, 217, 0.85)",
    backgroundHover: "rgba(255, 233, 217, 1)",
  },
  {
    border: "green030",
    text: "green080",
    background: "rgba(222, 251, 230, 0.85)",
    backgroundHover: "rgba(222, 251, 230, 1)",
  },
  {
    border: "yellow030",
    text: "yellow080",
    background: "rgba(255, 248, 193, 0.85)",
    backgroundHover: "rgba(255, 248, 193, 1)",
  },
  {
    border: "purple030",
    text: "purple080",
    background: "rgba(237, 228, 255, 0.85)",
    backgroundHover: "rgba(237, 228, 255, 1)",
  },
  {
    border: "darkNavy030",
    text: "darkNavy080",
    background: "rgba(231, 241, 255, 0.85)",
    backgroundHover: "rgba(231, 241, 255, 1)",
  },
];

export const getColorBySignerIndex = (index: number): MarkerColorType => {
  const adjustedIndex = (index - 1) % markerColors.length;
  return markerColors[adjustedIndex];
};

type FieldType = "Text" | "Dropdown" | "Address" | "Date" | "Follow writing" | "Signature" | boolean;

export function getFieldTypeWithAttr(key: DocumentSignerAttrType): FieldType {
  switch (true) {
    case /^INPUT-ITEM-/.test(key):
    case /^AGENT-INPUT-ITEM-/.test(key):
      return "Text";

    case /^DROPDOWN-ITEM-/.test(key):
    case /^AGENT-DROPDOWN-ITEM-/.test(key):
      return "Dropdown";

    case /^ADDRESS-ITEM-/.test(key):
    case /^AGENT-ADDRESS-ITEM-/.test(key):
      return "Address";

    case /^DATE-ITEM-/.test(key):
    case /^AGENT-DATE-ITEM-/.test(key):
      return "Date";

    case /^FOLLOW-WRITING-ITEM-/.test(key):
    case /^AGENT-FOLLOW-WRITING-ITEM-/.test(key):
      return "Follow writing";

    case /^AGENT-SIGNATURE(-IMAGE)?$/.test(key):
      return "Signature";

    default:
      return false;
  }
}

export type PdfPageSpecType = {
  page: number;
  width: number;
  height: number;
  top: number;
  baseWidth: number;
  baseHeight: number;
};

export type PdfRelativeSizeSpec = {
  width: number;
  height: number;
  baseWidth: number;
  baseHeight: number;
};

export type PdfPageScrollType = {
  scrollLeft: number;
  scrollTop: number;
};

export type PdfRelativeSizeReqType = {
  metainfo: DocsPdfMetainfo;
  zoom?: number;
  baseZoom: number;
  pageSpacingHeight: number;
};
